/**
 * L'ALLENAMENTO FRA AMICI, lato TMA: le letture del contratto del server e la «casella» del consenso (BACKGAMMON-COACH-AMICI-AUTOPLAY-20261101
 * rev.1 punto 1 e rev.2; disegno del server `31-disegno-coach-amici.md` §3; QGammon#161).
 *
 * ⛔ I CAMPI NUOVI SI LEGGONO COME DATI SCONOSCIUTI (V10 di tma-orcr, 01/11 22:47Z): i tipi installati sono 0.23.0 e non li dichiarano, e il bump a
 * 0.24.0 e' GO di Luca. Ogni lettura qui sotto prende un `unknown` e lo guarda con una guardia locale: un valore diverso da quello atteso vale «niente».
 *
 * LA CASELLA (perche' esiste). Gli invii stanno in codice condiviso con la produzione (`useInvite`, `useAccettaInvito`, `AcceptInviteSheet`), dove la
 * regola V11 vuole 0 byte diversi: lì non si aggiunge un parametro, solo un ramo che il bundler piega col letterale del flag. Il dato del consenso
 * arriva quindi da questo modulo, che solo quei rami leggono (in produzione il modulo non c'e'):
 *  · la PROPOSTA di chi invita: `proponiAllenamento(true)` subito prima di `createInvite`, e `prendiPropostaAllenamento()` la CONSUMA alla lettura:
 *    il prossimo invito, da qualunque schermata, riparte spento (default spento, testo di Luca);
 *  · la RISPOSTA di chi accetta: `registraRisposta(codice, …)` dal gesto esplicito, e `corpoAccettazione(codice)` la consuma una volta sola e solo per
 *    QUEL codice, entro `SCADENZA_RISPOSTA_MS`. Senza un gesto il corpo e' `{}`: per il server «campo assente» = senza allenamento (fail-closed).
 */

export type RispostaAllenamento = 'accetto' | 'rifiuto';
export type PostoAllenamento = 'W' | 'B';
export type MotivoNonAmmesso = 'spento' | 'premio' | 'ripresa' | 'board';
export const MOTIVI_NON_AMMESSO: readonly MotivoNonAmmesso[] = ['spento', 'premio', 'ripresa', 'board'];

/** L'esito di un allenamento proposto, all'accettazione (`invite:accepted` e la risposta dell'accept). */
export type EsitoAllenamento = { esito: 'attivo' | 'rifiutato' | 'non_ammesso'; motivo: MotivoNonAmmesso | null };

/** Lo stato dell'allenamento di un match, come lo porta `match:state.allenamento`. */
export type StatoAllenamento = { stato: 'attivo' | 'revocato'; revocatoDa: PostoAllenamento | null };

/** Il codice del 409 del server quando l'allenamento proposto non e' ammesso (`invites/routes.ts`, `ALLENAMENTO_NON_AMMESSO`). */
export const ALLENAMENTO_NON_AMMESSO = 'allenamento_non_ammesso';

/** Quanto vale una risposta registrata prima che parta l'accept: il gesto e la chiamata stanno nello stesso tocco. */
export const SCADENZA_RISPOSTA_MS = 60_000;

const casella: {
  proposta: boolean;
  risposte: Map<string, { risposta: RispostaAllenamento; ora: number }>;
  /** D5 della revisione di #959: l'esito NON attivo visto da chi invita, per match, finche' non lo chiude al tavolo. */
  esitiAlTavolo: Map<string, EsitoAllenamento>;
} = {
  proposta: false,
  risposte: new Map(),
  esitiAlTavolo: new Map(),
};

const chiaveCodice = (codice: string) => codice.trim().toUpperCase();
const posto = (x: unknown): PostoAllenamento | null => (x === 'W' || x === 'B' ? x : null);
const motivo = (x: unknown): MotivoNonAmmesso | null => (MOTIVI_NON_AMMESSO.includes(x as MotivoNonAmmesso) ? (x as MotivoNonAmmesso) : null);

/** Chi invita: la scelta dell'interruttore «Allenamento fra amici», da fissare SUBITO prima di `createInvite`. */
export function proponiAllenamento(acceso: boolean): void {
  casella.proposta = acceso;
}

/** `useInvite` (ramo del letterale): la proposta, CONSUMATA. Dopo la lettura la casella torna spenta. */
export function prendiPropostaAllenamento(): boolean {
  const p = casella.proposta;
  casella.proposta = false;
  return p;
}

/** Chi accetta: la risposta ESPLICITA (uno dei due bottoni) per QUEL codice, appena prima dell'accept. */
export function registraRisposta(codice: string, risposta: RispostaAllenamento, ora: number = Date.now()): void {
  casella.risposte.set(chiaveCodice(codice), { risposta, ora });
}

/**
 * Il corpo dell'accept (ramo del letterale): `{ allenamento: <risposta> }` se per QUEL codice c'e' una risposta fresca, altrimenti `{}`. La
 * risposta si consuma qui: un secondo accept dello stesso codice senza un gesto nuovo manda `{}` (cioe' senza allenamento).
 */
export function corpoAccettazione(codice: string, ora: number = Date.now()): { allenamento?: RispostaAllenamento } {
  const k = chiaveCodice(codice);
  const r = casella.risposte.get(k);
  casella.risposte.delete(k);
  if (!r || ora - r.ora > SCADENZA_RISPOSTA_MS || ora < r.ora) return {};
  return { allenamento: r.risposta };
}

/**
 * Chi invita: l'esito dell'allenamento proposto, quando NON e' attivo (rifiutato, non ammesso), si porta al tavolo di quel match (D5 della
 * revisione di #959): il foglio dell'invito lo mostra per 900 ms prima di andare al tavolo, e li' resta finche' l'utente non lo chiude.
 * Un esito attivo non serve: al tavolo c'e' gia' la riga dell'allenamento. Vive nella memoria della pagina (una ricarica lo perde).
 */
export function lasciaEsitoAlTavolo(matchId: string, esito: EsitoAllenamento): void {
  if (esito.esito === 'attivo') return;
  casella.esitiAlTavolo.set(matchId, esito);
}

/** Il tavolo: l'esito lasciato per QUESTO match, o `null`. */
export function esitoAlTavolo(matchId: string): EsitoAllenamento | null {
  return casella.esitiAlTavolo.get(matchId) ?? null;
}

/** Il tavolo: l'utente l'ha chiuso, non torna. */
export function chiudiEsitoAlTavolo(matchId: string): void {
  casella.esitiAlTavolo.delete(matchId);
}

/** Solo per i test: la casella vuota. */
export function svuotaCasellaAllenamento(): void {
  casella.proposta = false;
  casella.risposte.clear();
  casella.esitiAlTavolo.clear();
}

/** L'anteprima dell'invito (`GET /invites/:code`) propone l'allenamento? Solo il valore letterale `"proposto"`. */
export function propostoNellAnteprima(anteprima: unknown): boolean {
  return !!anteprima && typeof anteprima === 'object' && (anteprima as { allenamento?: unknown }).allenamento === 'proposto';
}

/** Il 409 della creazione: il `motivo` se e' `allenamento_non_ammesso` (un motivo che questa build non conosce vale `altro`), altrimenti `null`. */
export function motivoNonAmmessoDel409(corpo: unknown): MotivoNonAmmesso | 'altro' | null {
  if (!corpo || typeof corpo !== 'object') return null;
  const c = corpo as { error?: unknown; motivo?: unknown };
  if (c.error !== ALLENAMENTO_NON_AMMESSO) return null;
  return motivo(c.motivo) ?? 'altro';
}

/** L'esito dell'accettazione (`allenamento: { esito, motivo? }`), o `null` se manca o non si legge. */
export function leggiEsitoAllenamento(raw: unknown): EsitoAllenamento | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { esito?: unknown; motivo?: unknown };
  if (r.esito !== 'attivo' && r.esito !== 'rifiutato' && r.esito !== 'non_ammesso') return null;
  return { esito: r.esito, motivo: motivo(r.motivo) };
}

/** `match:state.allenamento`: `null` (assente, `null`, o non leggibile) oppure lo stato. */
export function leggiAllenamento(state: unknown): StatoAllenamento | null {
  if (!state || typeof state !== 'object') return null;
  const a = (state as { allenamento?: unknown }).allenamento;
  if (!a || typeof a !== 'object') return null;
  const s = (a as { stato?: unknown }).stato;
  if (s !== 'attivo' && s !== 'revocato') return null;
  return { stato: s, revocatoDa: s === 'revocato' ? posto((a as { revocatoDa?: unknown }).revocatoDa) : null };
}

/** `match:state.players.<posto>.coach`: il tutor di quel posto e' acceso? Assente o non booleano = spento. */
export function tutorDelPosto(state: unknown, p: PostoAllenamento): boolean {
  if (!state || typeof state !== 'object') return false;
  const players = (state as { players?: unknown }).players;
  if (!players || typeof players !== 'object') return false;
  const g = (players as Record<string, unknown>)[p];
  return !!g && typeof g === 'object' && (g as { coach?: unknown }).coach === true;
}

/**
 * ⛔ NESSUNA CONVERSIONE SENZA UN CONSENSO NUOVO (condizione 2 di tma-orcr, 22:05Z): lo stato che il TAVOLO mostra. Il punto fermo e' lo
 * snapshot della JOIN di questo match (`allaJoin`): se alla join il match non era nato in allenamento, nessuno snapshot successivo lo fa
 * diventare tale (il server non lo fa mai; il client non ci crede comunque). Da `attivo` si puo' solo andare a `revocato`, mai tornare.
 *  · `allaJoin === null` ⇒ `null` sempre;
 *  · `ora` revocato ⇒ revocato (da `attivo` o da `revocato` alla join);
 *  · `allaJoin` revocato e `ora` attivo ⇒ resta revocato (niente ritorno).
 */
export function allenamentoDelTavolo(allaJoin: StatoAllenamento | null, ora: StatoAllenamento | null): StatoAllenamento | null {
  if (allaJoin === null) return null;
  if (ora?.stato === 'revocato') return ora;
  if (allaJoin.stato === 'revocato') return allaJoin;
  return ora ?? allaJoin;
}
